import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { CUSTOM_SEVERITIES } from '../../../../../shared/featureFlags/flagNames';
import { Exploitability } from '../../../../../shared/vulns/types';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import CustomRiskScoreBadge from '../../../generic/CustomRiskScoreBadge';
import VulnBadge from '../../../generic/VulnBadge';
import { Header } from '../../../shared/vulns/Drawer';
import CVEBadge from '../../IssuesInbox/IssueItem/CVEBadge';
import EpssBadge from '../../IssuesInbox/IssueItem/EpssBadge';
import FixBadge from '../../IssuesInbox/IssueItem/FixBadge';
import KnownExploitBadge from '../../IssuesInbox/IssueItem/KnownExploitBadge';
import type { VulnIssue } from '../../../../../shared/issuesV2';

/**
 * VulnerabilityIssueDetailsHeader displays the title, cve, cvss, etc.
 * at the top of the vuln drawer.
 */
const VulnerabilityIssueDetailsHeader: VoidFunctionComponent = () => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnIssue;
  const locatorParts = useSelector(IssuesV2Store.selectors.getIssueDetailsSourceLocatorParts);
  const isLinuxDep = useSelector(IssuesV2Store.selectors.getIsLinuxDep);
  const isCsBinaryDep = useSelector(IssuesV2Store.selectors.getIsCsBinaryDep);
  const isCustomSeveritiesEnabled = useIsFeatureEnabled(CUSTOM_SEVERITIES);
  if (!issueDetails) {
    return null;
  }
  const { exploitability, remediation, customRiskScore } = issueDetails;
  const shouldShowCustomRiskScore = isCustomSeveritiesEnabled && customRiskScore;
  const shouldShowFixBadge = remediation && !isLinuxDep && !isCsBinaryDep;

  return (
    <Header
      title={issueDetails.title}
      locatorParts={locatorParts}
      badgesSlot={
        <>
          {shouldShowCustomRiskScore
            ? <CustomRiskScoreBadge score={customRiskScore.score} hasTooltip={false} />
            : <VulnBadge cvss={issueDetails.cvss} isTruncated hasTooltip={false} />}
          {issueDetails.epss && (
            <EpssBadge tooltipId={`epss-badge-${issueDetails.id}`} epss={issueDetails.epss} showTooltip={false} />
          )}
          {shouldShowFixBadge && (
            <FixBadge
              version={issueDetails.remediation.partialFix}
              distance={issueDetails.remediation.partialFixDistance}
              showTooltip={false}
            />
          )}
          {issueDetails.cve && <CVEBadge cve={issueDetails.cve} showTooltip={false} /> }
          {exploitability !== Exploitability.Unknown && <KnownExploitBadge showTooltip={false} />}
        </>
      }
    />
  );
};

export default VulnerabilityIssueDetailsHeader;
